import { Controller } from '@hotwired/stimulus'
import * as THREE from 'three'

/**
 * Interactive 3D Print Controller
 *
 * Creates a simple photo print that users can:
 * - Upload images to display OR use provided src URL
 * - Rotate by dragging (mouse or touch)
 * - Zoom with mouse wheel or pinch gesture
 * - Reset view position
 */
export default class extends Controller {
  static targets = ['container', 'imageUpload']

  static optionalTargets = ['imageUpload']

  static values = {
    src: String,
  }

  // ===== INITIALIZATION =====

  connect() {
    // remove element and clean up when the modal is closed
    window.addEventListener('ProductDetailsImageModalClosed', () => {
      this.element.remove()
    })

    // Auto-rotation settings
    this.autoRotate = true
    this.autoRotateSpeed = 0.004

    // Mouse interaction state
    this.isDragging = false
    this.previousMousePosition = { x: 0, y: 0 }

    // Touch interaction state
    this.touches = new Map() // Track multiple touches
    this.isRotating = false
    this.isPinching = false
    this.lastPinchDistance = 0
    this.previousTouchPosition = { x: 0, y: 0 }

    this.setupScene()
    this.setupCamera()
    this.setupRenderer()
    this.setupLights()
    this.setupPrintParameters()
    this.initializePrint()
    this.setupEventListeners()
    this.startAnimationLoop()
  }

  /**
   * Initialize the Three.js scene with white background
   */
  setupScene() {
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0xffffff)
  }

  /**
   * Setup perspective camera with 75° FOV
   * Camera position and aspect ratio based on container dimensions
   */
  setupCamera() {
    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    if (rect.width === 0 || rect.height === 0) {
      console.error('Container has no dimensions! Check your CSS.')
      this.camera = new THREE.PerspectiveCamera(75, 800 / 600, 0.1, 1000)
      this.camera.position.z = 5
      return
    }

    this.camera = new THREE.PerspectiveCamera(75, rect.width / rect.height, 0.1, 1000)
    this.setInitialCameraPosition()
  }

  /**
   * Calculate and set optimal camera position based on print dimensions
   */
  setInitialCameraPosition() {
    const printWidth = this.printWidth || 3
    const printHeight = this.printHeight || 2

    // Calculate distance needed to fit print in view
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(printWidth, printHeight)

    // Calculate distance with padding (multiply by 1.2 for closer framing of simple prints)
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.2

    // Ensure reasonable distance range for photo prints
    const minDistance = Math.max(distance, 2.0)
    const maxDistance = Math.min(minDistance, 7)

    this.camera.position.z = maxDistance
  }

  /**
   * Initialize WebGL renderer with proper color management and antialiasing
   * Attaches canvas to the container element
   */
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true })

    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    this.renderer.setSize(rect.width, rect.height)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.shadowMap.enabled = false // Shadows disabled for performance

    // Configure color management for proper image display
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.NoToneMapping
    this.renderer.toneMappingExposure = 1.0

    container.appendChild(this.renderer.domElement)
  }

  /**
   * Setup three-point lighting system:
   * - Ambient light for overall illumination
   * - Key light (main directional light)
   * - Fill light (softer secondary light)
   * - Top light (additional illumination)
   */
  setupLights() {
    // Soft ambient lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
    this.scene.add(ambientLight)

    // Main directional light (key light)
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.0)
    keyLight.position.set(2, 3, 4)
    keyLight.castShadow = false
    this.scene.add(keyLight)

    // Fill light from opposite side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4)
    fillLight.position.set(-2, -1, 3)
    fillLight.castShadow = false
    this.scene.add(fillLight)

    // Top light for even illumination
    const topLight = new THREE.DirectionalLight(0xffffff, 0.3)
    topLight.position.set(0, 5, 1)
    topLight.castShadow = false
    this.scene.add(topLight)
  }

  /**
   * Initialize print dimensions, materials, and interaction state
   */
  setupPrintParameters() {
    // Photo texture and material references with proper color settings
    this.photoTexture = new THREE.Texture()
    this.photoTexture.colorSpace = THREE.SRGBColorSpace
    this.photoTexture.flipY = true
    this.photoTexture.premultiplyAlpha = false
    this.photoTexture.format = THREE.RGBAFormat // Required for sRGB color space
    this.photoTexture.type = THREE.UnsignedByteType // Required for sRGB
    this.photoMaterial = null

    // Print dimensions (in Three.js units)
    this.printWidth = 3
    this.printHeight = 2
    this.printThickness = 0.02 // Very thin for a photo print

    // State tracking
    this.imageLoaded = false
  }

  /**
   * Create and add the initial print to the scene
   */
  initializePrint() {
    this.print = this.createPrint()
    this.scene.add(this.print)

    // Load image from src if provided
    if (this.hasSrcValue && this.srcValue) {
      this.loadImageFromSrc(this.srcValue)
    }
  }

  /**
   * Load image from src URL and update print
   * @param {string} srcUrl - URL of the image to load
   */
  loadImageFromSrc(srcUrl) {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Handle CORS if needed
    img.onload = () => {
    // Dispose of previous texture
      if (this.photoTexture.image) {
        this.photoTexture.dispose()
      }

      // Create new texture from loaded image with proper color settings
      const newTexture = new THREE.Texture(img)
      newTexture.needsUpdate = true
      newTexture.colorSpace = THREE.SRGBColorSpace
      newTexture.flipY = true // Proper orientation
      newTexture.premultiplyAlpha = false
      newTexture.format = THREE.RGBAFormat // Required for sRGB color space
      newTexture.type = THREE.UnsignedByteType // Required for sRGB

      // Update photo material with new texture
      if (this.photoMaterial) {
        this.photoMaterial.map = newTexture
        this.photoMaterial.needsUpdate = true
      }

      this.photoTexture = newTexture
      this.updatePrintDimensions(img)
    }
    img.onerror = (error) => {
      console.error(`Failed to load image from src: ${srcUrl}`, error)
    }
    img.src = srcUrl
  }

  // ===== PRINT CONSTRUCTION =====

  /**
   * Create the complete photo print
   * Consists of:
   * 1. Photo front (image display)
   * 2. White backing (rear surface)
   */
  createPrint() {
    const printGroup = new THREE.Group()

    // 1. PHOTO FRONT - Main image display using MeshBasicMaterial for accurate colors
    const photoGeometry = new THREE.PlaneGeometry(this.printWidth, this.printHeight)
    const photoMaterial = new THREE.MeshBasicMaterial({
      map: this.photoTexture,
      side: THREE.FrontSide,
    })
    this.photoMaterial = photoMaterial
    const photo = new THREE.Mesh(photoGeometry, photoMaterial)
    photo.position.z = this.printThickness / 2 // Front surface
    printGroup.add(photo)

    // 2. WHITE BACKING - Rear surface
    const backingGeometry = new THREE.PlaneGeometry(this.printWidth, this.printHeight)
    const backingMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff, // Pure white backing
      side: THREE.BackSide, // Only visible from behind
      roughness: 0.1, // Low roughness for reflectivity
      metalness: 0.0, // Non-metallic
    })
    const backing = new THREE.Mesh(backingGeometry, backingMaterial)
    backing.position.z = -this.printThickness / 2 // Back surface
    printGroup.add(backing)

    return printGroup
  }

  // ===== EVENT HANDLING =====

  /**
   * Setup all mouse, touch, and window event listeners
   */
  setupEventListeners() {
    // Mouse interaction for print rotation
    document.addEventListener('mousedown', this.handleMouseDown)
    document.addEventListener('mousemove', this.handleMouseMove)
    document.addEventListener('mouseup', this.handleMouseUp)

    // Touch interaction for print rotation and pinch zoom
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: false })
    this.element.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    this.element.addEventListener('touchend', this.handleTouchEnd, { passive: false })

    // Mouse wheel for zooming
    document.addEventListener('wheel', this.handleWheel, { passive: false })

    // Window resize handling
    window.addEventListener('resize', this.handleResize)

    // Image upload only if target exists
    if (this.hasImageUploadTarget) {
      this.imageUploadTarget.addEventListener('change', this.handleImageUpload.bind(this))
    }
  }

  // ===== MOUSE EVENT HANDLERS =====

  /**
   * Start drag interaction
   */
  handleMouseDown = () => {
    this.isDragging = true

    // Pause auto-rotation during manual interaction
    this.autoRotate = false
  }

  /**
   * Handle print rotation during drag
   * Rotates print based on mouse movement delta
   */
  handleMouseMove = (e) => {
    const deltaMove = {
      x: e.clientX - this.previousMousePosition.x,
      y: e.clientY - this.previousMousePosition.y,
    }

    // Rotate print if dragging
    if (this.isDragging && this.print) {
      this.print.rotation.y += deltaMove.x * 0.01 // Horizontal rotation
      this.print.rotation.x += deltaMove.y * 0.01 // Vertical rotation
    }

    // Track mouse position for next frame
    this.previousMousePosition = {
      x: e.clientX,
      y: e.clientY,
    }
  }

  /**
   * End drag interaction
   */
  handleMouseUp = () => {
    this.isDragging = false

    // Resume auto-rotation after a short delay
    setTimeout(() => {
      if (!this.isDragging && !this.isRotating && !this.isPinching) {
        this.autoRotate = true
      }
    }, 2000)
  }

  // ===== TOUCH EVENT HANDLERS =====

  /**
   * Handle touch start - supports single touch rotation and two-finger pinch zoom
   */
  handleTouchStart = (e) => {
    e.preventDefault() // Prevent scrolling and other default behaviors

    // Clear existing touches and track new ones
    this.touches.clear()

    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i]
      this.touches.set(touch.identifier, {
        x: touch.clientX,
        y: touch.clientY,
        startX: touch.clientX,
        startY: touch.clientY,
      })
    }

    const touchCount = this.touches.size

    if (touchCount === 1) {
      // Single touch - start rotation
      this.isRotating = true
      this.isPinching = false
      this.autoRotate = false // Pause auto-rotation

      const firstTouch = Array.from(this.touches.values())[0]
      this.previousTouchPosition = { x: firstTouch.x, y: firstTouch.y }
    }
    else if (touchCount === 2) {
      // Two touches - start pinch zoom
      this.isRotating = false
      this.isPinching = true
      this.autoRotate = false

      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      // Calculate initial distance between touches
      this.lastPinchDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )
    }
  }

  /**
   * Handle touch move - performs rotation or pinch zoom based on touch count
   */
  handleTouchMove = (e) => {
    e.preventDefault() // Prevent scrolling

    // Update touch positions
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i]
      if (this.touches.has(touch.identifier)) {
        const storedTouch = this.touches.get(touch.identifier)
        storedTouch.x = touch.clientX
        storedTouch.y = touch.clientY
        this.touches.set(touch.identifier, storedTouch)
      }
    }

    const touchCount = this.touches.size

    if (touchCount === 1 && this.isRotating && this.print) {
      // Single touch rotation
      const currentTouch = Array.from(this.touches.values())[0]

      const deltaMove = {
        x: currentTouch.x - this.previousTouchPosition.x,
        y: currentTouch.y - this.previousTouchPosition.y,
      }

      // Apply rotation with touch-friendly sensitivity
      this.print.rotation.y += deltaMove.x * 0.008 // Slightly more sensitive than mouse
      this.print.rotation.x += deltaMove.y * 0.008

      // Update previous position
      this.previousTouchPosition = { x: currentTouch.x, y: currentTouch.y }
    }
    else if (touchCount === 2 && this.isPinching) {
      // Two-finger pinch zoom
      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      // Calculate current distance between touches
      const currentDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )

      // Calculate zoom delta
      const deltaDistance = currentDistance - this.lastPinchDistance
      const zoomSensitivity = 0.01

      // Apply zoom to camera
      this.camera.position.z -= deltaDistance * zoomSensitivity
      this.camera.position.z = Math.max(2, Math.min(this.camera.position.z, 10))

      // Update last distance for next calculation
      this.lastPinchDistance = currentDistance
    }
  }

  /**
   * Handle touch end - cleanup touch tracking and resume auto-rotation
   */
  handleTouchEnd = (e) => {
    e.preventDefault()

    // Remove ended touches from tracking
    const activeTouchIds = Array.from(e.touches).map(touch => touch.identifier)

    // Remove touches that are no longer active
    for (let [touchId] of this.touches) {
      if (!activeTouchIds.includes(touchId)) {
        this.touches.delete(touchId)
      }
    }

    const remainingTouches = this.touches.size

    if (remainingTouches === 0) {
      // All touches ended
      this.isRotating = false
      this.isPinching = false

      // Resume auto-rotation after a delay
      setTimeout(() => {
        if (!this.isRotating && !this.isPinching && !this.isDragging) {
          this.autoRotate = true
        }
      }, 2000) // Wait 2 seconds before resuming auto-rotation
    }
    else if (remainingTouches === 1 && this.isPinching) {
      // Transitioned from pinch to single touch - switch to rotation
      this.isPinching = false
      this.isRotating = true

      const remainingTouch = Array.from(this.touches.values())[0]
      this.previousTouchPosition = { x: remainingTouch.x, y: remainingTouch.y }
    }
    else if (remainingTouches === 2 && this.isRotating) {
      // Added second touch - switch to pinch zoom
      this.isRotating = false
      this.isPinching = true

      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      this.lastPinchDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )
    }
  }

  /**
   * Handle zoom with mouse wheel
   * Constrains camera distance between 2 and 10 units
   */
  handleWheel = (e) => {
    this.camera.position.z += e.deltaY * 0.01
    this.camera.position.z = Math.max(2, Math.min(this.camera.position.z, 10))
    e.preventDefault()
  }

  /**
   * Handle window resize - update camera and renderer
   */
  handleResize = () => {
    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    this.camera.aspect = rect.width / rect.height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(rect.width, rect.height)

    // Recalculate optimal camera position for new container size
    if (this.print && this.imageLoaded) {
      this.adjustCameraForPrint()
    }
    else {
    // If no image loaded yet, use default dimensions
      this.setInitialCameraPosition()
    }
  }

  // ===== IMAGE HANDLING =====

  /**
   * Update print dimensions to match uploaded image aspect ratio
   * Maintains total print area while adjusting proportions
   */
  updatePrintDimensions(image) {
    if (!image) return

    const aspectRatio = image.width / image.height
    const area = this.printWidth * this.printHeight

    // Calculate new dimensions maintaining area
    this.printHeight = Math.sqrt(area / aspectRatio)
    this.printWidth = this.printHeight * aspectRatio

    this.rebuildPrint()
    this.imageLoaded = true

    // Adjust camera position for new print size
    this.adjustCameraForPrint()
  }

  /**
   * Adjust camera position based on current print dimensions
   */
  adjustCameraForPrint() {
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(this.printWidth, this.printHeight)

    // Calculate optimal distance with padding
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.2
    const targetZ = Math.max(distance, 2.0)
    const finalZ = Math.min(targetZ, 7)

    // Smoothly animate to new position
    const startZ = this.camera.position.z
    const duration = 900 // 0.9 seconds for quick transition
    const startTime = Date.now()

    const animateCamera = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Smooth easing function
      const easeProgress = progress * progress * (3 - 2 * progress)

      this.camera.position.z = startZ + (finalZ - startZ) * easeProgress

      if (progress < 1) {
        requestAnimationFrame(animateCamera)
      }
    }

    animateCamera()
  }

  /**
   * Completely rebuild the print geometry
   * Used when dimensions change
   */
  rebuildPrint() {
    if (this.print) {
      // Remove from scene
      this.scene.remove(this.print)

      // Dispose of all geometries and materials for memory cleanup
      this.print.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose()
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => {
              // Don't dispose the photo material as it's reused
              if (material !== this.photoMaterial) {
                if (material.map) material.map.dispose()
                material.dispose()
              }
            })
          }
          else {
            if (object.material !== this.photoMaterial) {
              if (object.material.map) object.material.map.dispose()
              object.material.dispose()
            }
          }
        }
      })
    }

    // Create new print on next animation frame
    requestAnimationFrame(() => {
      this.print = this.createPrint()
      this.scene.add(this.print)
    })
  }

  // ===== ANIMATION =====

  /**
   * Start the render loop
   */
  startAnimationLoop() {
    this.animate()
  }

  /**
   * Main animation loop - renders the scene continuously
   */
  animate() {
    requestAnimationFrame(this.animate.bind(this))

    // Auto-rotate the print if enabled and not being manually interacted with
    if (this.autoRotate && !this.isDragging && !this.isRotating && !this.isPinching && this.print) {
      this.print.rotation.y += this.autoRotateSpeed
    }

    this.renderer.render(this.scene, this.camera)
  }

  // ===== PUBLIC METHODS (called from HTML/Stimulus actions) =====

  /**
   * Reset camera and print to default position and rotation
   */
  resetView() {
    if (this.print) {
    // Smoothly animate print rotation back to zero
      const startRotationX = this.print.rotation.x
      const startRotationY = this.print.rotation.y
      const duration = 600 // 0.6 seconds
      const startTime = Date.now()

      const animateReset = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        // Smooth easing
        const easeProgress = progress * progress * (3 - 2 * progress)

        this.print.rotation.x = startRotationX * (1 - easeProgress)
        this.print.rotation.y = startRotationY * (1 - easeProgress)

        if (progress < 1) {
          requestAnimationFrame(animateReset)
        }
        else {
        // Resume auto-rotation after reset
          this.autoRotate = true
        }
      }

      animateReset()
    }

    // Reset camera to optimal position
    this.adjustCameraForPrint()
  }

  /**
   * Trigger file upload dialog for new image
   */
  uploadImage() {
    // Don't allow file upload if src is provided
    if (this.hasSrcValue && this.srcValue) {
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = this.handleImageUpload.bind(this)
    input.click()
  }

  /**
   * Process uploaded image file
   * Creates texture and updates print dimensions
   */
  handleImageUpload(e) {
  // Don't allow file upload if src is provided
    if (this.hasSrcValue && this.srcValue) {
      return
    }

    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
    // Dispose of previous texture
      if (this.photoTexture.image) {
        this.photoTexture.dispose()
      }

      const image = new Image()
      image.onload = () => {
      // Create new texture from image with proper color settings
        const newTexture = new THREE.Texture(image)
        newTexture.needsUpdate = true
        newTexture.colorSpace = THREE.SRGBColorSpace
        newTexture.flipY = true // Proper orientation
        newTexture.premultiplyAlpha = false
        newTexture.format = THREE.RGBAFormat // Required for sRGB color space
        newTexture.type = THREE.UnsignedByteType // Required for sRGB

        // Update photo material with new texture
        if (this.photoMaterial) {
          this.photoMaterial.map = newTexture
          this.photoMaterial.needsUpdate = true
        }

        this.photoTexture = newTexture
        this.updatePrintDimensions(image)
      }
      image.src = event.target.result
    }
    reader.readAsDataURL(file)
  }

  // ===== CLEANUP =====

  /**
   * Cleanup when controller disconnects
   * Removes event listeners and disposes of Three.js resources
   */
  disconnect() {
    // Remove mouse event listeners
    document.removeEventListener('mousedown', this.handleMouseDown)
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)

    // Remove touch event listeners
    this.element.removeEventListener('touchstart', this.handleTouchStart)
    this.element.removeEventListener('touchmove', this.handleTouchMove)
    this.element.removeEventListener('touchend', this.handleTouchEnd)

    // Remove other event listeners
    document.removeEventListener('wheel', this.handleWheel)
    window.removeEventListener('resize', this.handleResize)

    // Dispose of textures and materials
    if (this.photoTexture) {
      this.photoTexture.dispose()
    }

    if (this.photoMaterial) {
      this.photoMaterial.dispose()
    }

    // Dispose of all print geometries and materials
    if (this.print) {
      this.print.traverse((object) => {
        if (object.geometry) object.geometry.dispose()
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose())
          }
          else {
            object.material.dispose()
          }
        }
      })
    }

    // Dispose of renderer
    if (this.renderer) {
      this.renderer.dispose()
    }

    // Clear touch tracking
    if (this.touches) {
      this.touches.clear()
    }
  }
}
